import {
  FC,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  isValidElement,
} from 'react';
import {
  Button,
  Flex,
  BoxV2 as Box,
  TextInput,
  MultimediaList,
  AttachmentInfo,
  AttachmentPreview,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImage } from '@fortawesome/pro-regular-svg-icons';
import { capitalize, debounce } from 'lodash-es';

import Modal from '@/components/ModalV2';
import { Tabs, TabPanel } from './Tabs';
import {
  MODAL_TITLE,
  Tab,
  FILE_SIZE_LIMIT,
  ASSET_DIMENSIONS,
  ASSET_THUMBNAIL_WIDTH,
} from './constants';
import AssetImageUpload from './AssetImageUpload';
import {
  BrandAssetType,
  brandAssetController,
  attachmentController,
  AttachmentFolder,
} from '@/apis/core';
import { toastFlashMessage } from '@/utils';

interface AssetUploadModalProps {
  assetType: BrandAssetType;
  defaultActiveTab?: Tab;
  brandUid: string;
  open: boolean;
  dialogTitle?: string;
  dialogDescription?: ReactNode | Partial<Record<Tab, ReactNode>>;
  panelDescription?: ReactNode | Partial<Record<Tab, ReactNode>>;
  confirmBtnText?: string;
  verifyOnUpload?: boolean;
  uploadPermission?: boolean;
  deletePermission?: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onUpdate?: (attachments: AttachmentInfo[]) => void;
}

const TABS = [
  { id: Tab.Image, label: 'Image Upload' },
  { id: Tab.Link, label: 'Link Upload' },
];

const AssetUploadModal: FC<AssetUploadModalProps> = ({
  open,
  assetType: type,
  defaultActiveTab = Tab.Image,
  brandUid,
  dialogTitle,
  dialogDescription,
  panelDescription,
  confirmBtnText = 'Upload',
  verifyOnUpload = false,
  deletePermission = true,
  uploadPermission = true,
  onClose,
  onUpdate,
  onSubmit,
}) => {
  const getDesc = (
    desc: ReactNode | Partial<Record<Tab, ReactNode>> | undefined,
    tab: Tab
  ): ReactNode => {
    if (desc && typeof desc === 'object' && !isValidElement(desc)) {
      return (desc as Partial<Record<Tab, ReactNode>>)[tab];
    }
    return desc as ReactNode;
  };

  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  const dialogDesc = getDesc(dialogDescription, activeTab);
  const panelDescImage = getDesc(panelDescription, Tab.Image);
  const panelDescLink = getDesc(panelDescription, Tab.Link);

  const [link, setLink] = useState('');
  const [imageUploads, setImageUploads] = useState<AttachmentInfo[]>([]);
  const [linkUploads, setLinkUploads] = useState<AttachmentInfo[]>([]);
  const [previewImage, setPreviewImage] = useState<AttachmentInfo>();
  const [proceeding, setProceeding] = useState(false);
  const [linkError, setLinkError] = useState('');
  const lowerCaseAssetType = type.toLocaleLowerCase();

  useEffect(() => {
    if (!open) {
      setLink('');
      setImageUploads([]);
      setLinkUploads([]);
      setLinkError('');
      setProceeding(false);
      setActiveTab(defaultActiveTab);
    }
  }, [open, defaultActiveTab]);

  const handleModalClose = () => {
    onClose();
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as Tab);
  };

  const registerAsset = async (upload: AttachmentInfo) => {
    const result = await brandAssetController.registerBrandAsset(
      brandUid,
      type.toUpperCase(),
      upload.fileName,
      upload.uuid
    );
    if (result && verifyOnUpload) {
      await brandAssetController.verifyAsset(brandUid, upload.fileName);
    }
    return result;
  };

  const handleBrandAssetRegistration = async (uploads: AttachmentInfo[]) => {
    setProceeding(true);
    const results = await Promise.allSettled(
      uploads.map((upload) => registerAsset(upload))
    );
    const succeededUploads: AttachmentInfo[] = [];
    const failedUploads: AttachmentInfo[] = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        showSuccessMessage(result.value.name);
        succeededUploads.push(uploads[index]);
      } else {
        failedUploads.push(uploads[index]);
      }
    });
    if (succeededUploads.length > 0) {
      onUpdate?.(succeededUploads);
    }

    if (failedUploads.length > 0) {
      if (activeTab === Tab.Image) {
        setImageUploads(failedUploads);
      } else {
        setLinkUploads(failedUploads);
      }
    } else {
      onSubmit?.();
    }
    setProceeding(false);
  };

  const showSuccessMessage = (assetName: string) => {
    toastFlashMessage(
      verifyOnUpload
        ? `${assetName} successfully uploaded and verified`
        : `${assetName} successfully uploaded`,
      'success'
    );
  };

  const handleConfirmClick = () => {
    if (activeTab === Tab.Image) {
      handleBrandAssetRegistration(imageUploads);
    } else if (activeTab === Tab.Link) {
      handleBrandAssetRegistration(linkUploads);
    }
  };

  const validateLinkUrl = useCallback(
    debounce((url: string) => {
      setLinkUploads([]);
      setLinkError('');
      if (!url) {
        return;
      }

      setProceeding(true);
      (async () => {
        const attachment = await attachmentController.uploadByUrl(
          {
            folderType:
              type === BrandAssetType.Logo
                ? AttachmentFolder.BrandAssetLogo
                : AttachmentFolder.BrandAssetBanner,
            url,
          },
          { silent: true }
        );

        if ('uuid' in attachment && attachment.uuid) {
          const file = await attachmentController.downloadAttachment({
            uuid: attachment.uuid,
          });
          if (file) {
            setLinkUploads([
              {
                ...attachment,
                file,
              },
            ]);
          }
        }
        if ('error' in attachment) {
          setLinkError(attachment.error);
        }
        setProceeding(false);
      })();
    }, 500),
    [type]
  );

  const handleLinkUrlChange = (url: string) => {
    setLink(url);
    validateLinkUrl(url);
  };

  const handlePreview = (attachment: AttachmentInfo) => {
    setPreviewImage(attachment);
  };

  const handleDeleteLinkFile = (idx: number) => {
    setLinkUploads(linkUploads.filter((_, i) => i !== idx));
  };

  return (
    <Modal
      open={open}
      width="973px"
      onClose={handleModalClose}
      data-testid="assetUploadModal"
    >
      <Flex
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          color: 't.black80',
          width: '100%',
        }}
      >
        <Flex
          sx={{
            gap: 'm',
            fontSize: 'H600',
            alignItems: 'center',
            justifyContent: 'center',
            py: 's',
            fontWeight: 'semiBold',
            mb: 'xs',
          }}
        >
          <div>
            <FontAwesomeIcon icon={faFileImage} fontSize="24" />
          </div>
          <div>{dialogTitle || MODAL_TITLE[type]}</div>
        </Flex>
        {dialogDesc && (
          <Box sx={{ paddingTop: '11px', paddingBottom: '17px' }}>
            {dialogDesc}
          </Box>
        )}
        <Tabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange}>
          <Box sx={{ color: 't.black70', lineHeight: 'normal' }}>
            <TabPanel value={Tab.Image} hidden={activeTab !== Tab.Image}>
              {panelDescImage && <Box sx={{ mb: 'm' }}>{panelDescImage}</Box>}
              <AssetImageUpload
                brandAssetType={type}
                dimensions={ASSET_DIMENSIONS[type]}
                fileLimitKB={FILE_SIZE_LIMIT[type]}
                thumbnailWidth={ASSET_THUMBNAIL_WIDTH[type]}
                files={imageUploads}
                onChange={setImageUploads}
                onProcessingChange={setProceeding}
                onPreview={handlePreview}
                deletePermission={deletePermission}
                uploadPermission={uploadPermission}
              />
            </TabPanel>
            <TabPanel value={Tab.Link} hidden={activeTab !== Tab.Link}>
              {panelDescLink && <Box sx={{ mb: 'm' }}>{panelDescLink}</Box>}
              {uploadPermission && (
                <TextInput
                  style={{ width: '395px', marginBottom: '16px' }}
                  label={`${capitalize(lowerCaseAssetType)} URL`}
                  value={link}
                  onChange={handleLinkUrlChange}
                  disabled={proceeding}
                  data-testid="assetUploadModalLinkInput"
                  error={!!linkError}
                  note={linkError}
                />
              )}
              <div style={{ height: linkUploads.length ? 'auto' : '90px' }}>
                <MultimediaList
                  editable={deletePermission}
                  thumbnail
                  thumbnailWidth={ASSET_THUMBNAIL_WIDTH[type]}
                  downloadable={false}
                  title={`${capitalize(lowerCaseAssetType)} File`}
                  attachments={linkUploads}
                  onSelect={(idx: number) => handlePreview(linkUploads[idx])}
                  onDelete={handleDeleteLinkFile}
                  loading={proceeding}
                  data-testid="assetsList"
                  emptyMessage="No files have been linked"
                />
              </div>
            </TabPanel>
          </Box>
        </Tabs>
        <Flex
          sx={{ justifyContent: 'center', gap: 'm', mt: '30px', mb: 'xxxl' }}
        >
          <Button
            variant="outline"
            onClick={handleModalClose}
            data-testid="assetUploadModalCancelBtn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={
              proceeding ||
              (!linkUploads.length && activeTab === Tab.Link) ||
              (!imageUploads.length && activeTab === Tab.Image)
            }
            data-testid="assetUploadModalConfirmBtn"
          >
            {proceeding ? 'Processing...' : confirmBtnText}
          </Button>
        </Flex>
      </Flex>
      {!!previewImage?.file && (
        <AttachmentPreview
          file={previewImage.file}
          onClose={() => setPreviewImage(undefined)}
          mimeType={previewImage.mimeType}
          style={{ zIndex: 1000 }}
        />
      )}
    </Modal>
  );
};

export default AssetUploadModal;
